import React, { useState, useEffect } from 'react';
import { Sparkles, TrendingUp, TrendingDown, Minus, RefreshCw, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';

const AiMarketSummary = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchSummary();
    // Auto-refresh every 15 minutes
    const interval = setInterval(fetchSummary, 15 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/ai/market-summary`, { timeout: 60000 });
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching AI summary:', err);
      setError(err.response?.data?.detail || 'Unable to load market summary. Retrying...');
      // Auto-retry once after 5 seconds
      setTimeout(async () => {
        try {
          const retryResponse = await axios.get(`${API}/ai/market-summary`, { timeout: 60000 });
          setSummary(retryResponse.data);
          setError(null);
        } catch (retryErr) {
          setError('Market summary temporarily unavailable. Click retry.');
        }
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'bullish':
        return <TrendingUp className="text-green-400" size={20} />;
      case 'bearish':
        return <TrendingDown className="text-red-400" size={20} />;
      default:
        return <Minus className="text-yellow-400" size={20} />;
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'bullish':
        return 'text-green-400 bg-green-400/10 border-green-400/30';
      case 'bearish':
        return 'text-red-400 bg-red-400/10 border-red-400/30';
      default:
        return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-[#C5A059]/20 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#C5A059]/20 rounded-lg flex items-center justify-center animate-pulse">
            <Sparkles className="text-[#C5A059]" size={20} />
          </div>
          <div className="h-6 bg-white/10 rounded w-48 animate-pulse"></div>
        </div>
        <div className="space-y-3">
          <div className="h-4 bg-white/10 rounded w-full animate-pulse"></div>
          <div className="h-4 bg-white/10 rounded w-3/4 animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-white/10 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
            <AlertCircle className="text-red-400" size={20} />
          </div>
          <h3 className="font-manrope font-semibold text-white text-lg">AI Market Summary</h3>
        </div>
        <p className="text-gray-400 text-sm mb-4">{error}</p>
        <Button
          onClick={fetchSummary}
          variant="outline"
          size="sm"
          className="border-white/20 text-white hover:bg-white/5"
        >
          <RefreshCw size={14} className="mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div 
      className="bg-gradient-to-br from-[#111111] via-[#0F0F0F] to-[#0A0A0A] border border-[#C5A059]/20 rounded-lg p-6 hover:border-[#C5A059]/40 transition-all duration-300"
      data-testid="ai-market-summary"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#C5A059] to-[#D4AF37] rounded-lg flex items-center justify-center shadow-lg shadow-[#C5A059]/20">
            <Sparkles className="text-black" size={20} />
          </div>
          <div>
            <h3 className="font-manrope font-semibold text-white text-lg">AI Market Intelligence</h3>
            <p className="text-gray-500 text-xs">
              Updated {summary?.generated_at ? new Date(summary.generated_at).toLocaleTimeString() : 'recently'}
            </p>
          </div>
        </div>
        
        {/* Sentiment Badge */}
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${getSentimentColor(summary?.market_sentiment)}`}>
          {getSentimentIcon(summary?.market_sentiment)}
          <span className="text-sm font-medium capitalize">{summary?.market_sentiment || 'Neutral'}</span>
        </div>
      </div>

      {/* Summary */}
      <p className="text-gray-300 text-sm leading-relaxed mb-4">
        {summary?.summary}
      </p>

      {/* Key Insights */}
      {summary?.key_insights && summary.key_insights.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-[#C5A059] text-xs font-semibold uppercase tracking-wider">Key Insights</h4>
          <ul className="space-y-1.5">
            {summary.key_insights.map((insight, index) => (
              <li 
                key={index} 
                className="flex items-start gap-2 text-gray-400 text-sm"
              >
                <span className="text-[#C5A059] mt-1">•</span>
                <span>{insight}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
        <span className="text-gray-500 text-xs">
          Based on {summary?.news_count || 0} recent news articles
        </span>
        <span className="text-[#C5A059]/60 text-xs flex items-center gap-1">
          <Sparkles size={12} />
          Powered by AI
        </span>
      </div>
    </div>
  );
};

export default AiMarketSummary;
